import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
    ToggleChainActiveStatusParams,
    useGetPublicItemsLockerChains,
    useToggleChainActiveStatus,
} from '@core/api/usePublicItemsLocker';
import useTableQuickSearch from '@core/hooks/useTableQuickSearch';
import useCountryOptions, { useCountryName } from '@core/hooks/useCountryOptions';
import { ExportPopupOptions } from '@vms/ExportPopup/ExportPopup';
import { useExportPublicItemsXls } from '@core/api/usePublicItemsLockerFile';
import { RequiredCountryId } from '@core/api/useSurveys';

const DEFAULT_MINIMUM_CHAIN_UNITS = '50';

enum PublicItemsSearchParams {
    COUNTRY_ID = 'countryId',
    CHAIN_UNIT_MINIMUM = 'chainUnitMinimum',
}

const usePublicItemsLocker = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [isExportPopupOpen, setIsExportPopupOpen] = useState(false);
    // We use RequiredCountryId because we don't have enum support in CountryDetail's id!
    const [selectedCountryId, setSelectedCountryId] = useState<RequiredCountryId>();
    const [chainUnitsInput, setChainUnitsInput] = useState<string>(DEFAULT_MINIMUM_CHAIN_UNITS);
    const [activeChainUnitsMinimum, setActiveChainUnitsMinimum] = useState<string | null>(DEFAULT_MINIMUM_CHAIN_UNITS);
    const [isReadyForFetch, setIsReadyForFetch] = useState(false);

    const {
        doFetch: toggleChainActiveStatus,
        isLoading: isToggleLoading
    } = useToggleChainActiveStatus();

    const {
        data,
        isLoading,
        doFetch: doFetchChains
    } = useGetPublicItemsLockerChains();

    const { doFetchAndDownload } = useExportPublicItemsXls();

    const {
        searchValue,
        setSearchValue,
        searchResults
    } = useTableQuickSearch(data ?? [], (item) => item?.name);

    const { countryOptions } = useCountryOptions();
    const countryName = useCountryName(selectedCountryId ?? 1);

    const onExportPublicItems = useCallback(
        (options: ExportPopupOptions) => {
            if (!selectedCountryId) {
                return;
            }

            doFetchAndDownload({
                countryId: selectedCountryId,
                ...options,
            });

            setIsExportPopupOpen(false);
        },
        [doFetchAndDownload, selectedCountryId],
    );

    const onFetchChains = useCallback(() => {
        doFetchChains({
            params: {
                countryId: selectedCountryId,
                ...(activeChainUnitsMinimum ? { chainUnitMinimum: activeChainUnitsMinimum } : {}),
            },
        });
    }, [activeChainUnitsMinimum, doFetchChains, selectedCountryId]);

    const onToggleChainActiveStatus = useCallback(
        (params: ToggleChainActiveStatusParams) => {
            toggleChainActiveStatus({
                params,
            }).then(() => onFetchChains());
        },
        [onFetchChains, toggleChainActiveStatus],
    );

    const onUpdateChainUnitMinimum = useCallback(() => {
        if (!chainUnitsInput) {
            setChainUnitsInput('0');
        }

        setActiveChainUnitsMinimum(chainUnitsInput || '0');
        setSearchParams((prev) => {
            prev.set(PublicItemsSearchParams.CHAIN_UNIT_MINIMUM, chainUnitsInput || '0');
            return prev;
        });
    }, [chainUnitsInput, setSearchParams]);

    useEffect(() => {
        if (selectedCountryId) {
            setIsReadyForFetch(true);
            onFetchChains();
            setSearchParams((searchParams) => {
                searchParams.set(PublicItemsSearchParams.COUNTRY_ID, String(selectedCountryId));
                return searchParams;
            });
        }
    }, [selectedCountryId, onFetchChains, setSearchParams]);

    useEffect(() => {
        if (countryOptions && countryOptions.length > 0) {
            const countryIdFromUrl = Number(searchParams.get(PublicItemsSearchParams.COUNTRY_ID)) as RequiredCountryId;
            const chainUnitMinimumFromUrl =
                searchParams.get(PublicItemsSearchParams.CHAIN_UNIT_MINIMUM) ?? DEFAULT_MINIMUM_CHAIN_UNITS;

            setSelectedCountryId(countryIdFromUrl || countryOptions[0]?.id);
            setActiveChainUnitsMinimum(chainUnitMinimumFromUrl);
            setChainUnitsInput(chainUnitMinimumFromUrl);
        }
    }, [countryOptions, searchParams]);

    return {
        country: {
            selectedCountryId,
            setSelectedCountryId,
            countryOptions,
            countryName,
        },
        chains: {
            onToggleChainActiveStatus,
            onUpdateChainUnitMinimum,
            chainUnitsInput,
            setChainUnitsInput,
        },
        search: {
            results: searchResults,
            isLoading: isLoading || isToggleLoading || !isReadyForFetch,
            searchValue,
            setSearchValue,
        },
        exportXls: {
            isExportPopupOpen,
            setIsExportPopupOpen,
            onExportPublicItems,
        },
    };
};

export default usePublicItemsLocker;
